import { Input } from "antd";
import React, { useMemo, memo, useState, useEffect } from "react";
import moment from "moment";

import { COMPONENT_TYPE } from "../../../utils/FormMapper/XMLInputType";
import { FormLabel } from "../Components/FormLabel";
import { print_when_component_is_rendered } from "../../../../utils/develop";
import { Group } from "../Components/Group";
import { CommonComponentsProps } from "../../../types/common";
import { compareProps, getFullPathRow } from "../handle/inputHandle";
import { useInputHandle } from "../hooks/InputHandle";
import { Utilitaries } from "../../../../business";
import { highlightsErrors } from "../../../utils/functions";

interface ComponentsPropertiesProps extends CommonComponentsProps {}

const _QADate: React.FC<ComponentsPropertiesProps> = ({
    formState,
    componentsProperties,
    renderContext,
    tableRow,
    formInputDependenceState,
    inputCalcTrigger,
    handleOnInputChangeValue,
    justSee,
    triedSave,
}) => {
    const [showError, setShowError] = useState(false);

    print_when_component_is_rendered(componentsProperties);

    useEffect(() => setShowError(triedSave), [triedSave]);

    const fullPathRow = getFullPathRow(componentsProperties, tableRow?.row_key);
    const full_path = tableRow?.row_key ? fullPathRow : componentsProperties?.full_path;

    const valueToFormat =
        renderContext === "column" ? tableRow?.row_data?.[componentsProperties.key] : componentsProperties.value;

    const {
        status: whenValueChangeStatus,
        componentValue,
        hasUpdatedValue,
        calculated,
        required,
        restriction,
        visible,
        relevant,
        readOnly,
        error,
    } = useInputHandle({
        componentProperties: componentsProperties,
        when_value_changed_dependence: formInputDependenceState?.[full_path || ""],
        calcTrigger: inputCalcTrigger?.[componentsProperties?.full_path || ""],
        calcRowTrigger: inputCalcTrigger?.[fullPathRow],
        formState,
        rowTable: tableRow,
        showError: triedSave || showError,
        value: valueToFormat,
    });

    useEffect(() => {
        if (hasUpdatedValue > 0 && whenValueChangeStatus !== "IDLE") {
            handleOnInputChangeValue({
                value: componentValue,
                componentsProperties,
                row_key: tableRow?.row_key,
                full_path,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasUpdatedValue]);

    useEffect(() => {
        if (
            componentsProperties.calculate &&
            (!Utilitaries.isEmpty(inputCalcTrigger?.[componentsProperties?.full_path || ""]) ||
                !Utilitaries.isEmpty(inputCalcTrigger?.[fullPathRow]) ||
                componentsProperties.persist === "nao")
        ) {
            handleOnInputChangeValue({
                value: calculated,
                componentsProperties,
                row_key: tableRow?.row_key,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calculated]);

    const value = useMemo(() => {
        if (!valueToFormat) {
            return undefined;
        } else {
            const date = moment(valueToFormat, "YYYY-MM-DD");
            return date.isValid() ? date.format("YYYY-MM-DD") : undefined;
        }
    }, [valueToFormat]);

    if (!visible || !relevant) {
        return null;
    }

    return (
        <Group componentsProperties={componentsProperties} renderContext={renderContext}>
            <FormLabel componentsProperties={componentsProperties} required={required} renderContext={renderContext}>
                {componentsProperties.type === COMPONENT_TYPE.DATE && (
                    <Input
                        type="date"
                        value={value}
                        disabled={justSee || readOnly}
                        onChange={(date) => {
                            handleOnInputChangeValue({
                                value: date.target.value,
                                componentsProperties,
                                row_key: tableRow?.row_key,
                                full_path,
                            });
                        }}
                        style={highlightsErrors({ value, required, restriction })}
                    />
                )}

                {showError && error && <span style={{ color: "red" }}>{error}</span>}
            </FormLabel>
        </Group>
    );
};

export const QADate = memo(_QADate, (prev, next) => compareProps<ComponentsPropertiesProps>(prev, next));
